import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { storageService } from '../utils/storage';
import { tipoCambioService } from '../services/api/tipoCambio.service';
import type { TipoCambio } from '../modelos/TipoCambio';

interface TipoCambioContextValue {
  tipoCambio: TipoCambio | null;
  sinTipoCambio: boolean;
  refrescar: () => Promise<void>;
}

const TipoCambioContext = createContext<TipoCambioContextValue | undefined>(undefined);

function esDiaHoy(fecha: string): boolean {
  const hoy = new Date().toISOString().slice(0, 10);
  return fecha === hoy;
}

export function TipoCambioProvider({ children }: { children: ReactNode }) {
  const [tipoCambio, setTipoCambio] = useState<TipoCambio | null>(null);
  const [sinTipoCambio, setSinTipoCambio] = useState(false);

  const cargar = useCallback(async () => {
    const guardado = storageService.getTipoCambio();
    if (guardado && esDiaHoy(guardado.fecha)) {
      setTipoCambio(guardado);
      setSinTipoCambio(false);
      return;
    }

    try {
      const res = await tipoCambioService.getHoy();
      if (res.data) {
        storageService.saveTipoCambio(res.data);
        setTipoCambio(res.data);
        setSinTipoCambio(false);
      } else {
        setSinTipoCambio(true);
      }
    } catch {
      setSinTipoCambio(true);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  return (
    <TipoCambioContext.Provider value={{ tipoCambio, sinTipoCambio, refrescar: cargar }}>
      {children}
    </TipoCambioContext.Provider>
  );
}

export function useTipoCambio(): TipoCambioContextValue {
  const context = useContext(TipoCambioContext);
  if (!context) throw new Error('useTipoCambio debe usarse dentro de TipoCambioProvider');
  return context;
}
